import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Target,
  Eye,
  Users,
  GraduationCap,
  Handshake,
  Award,
  ShieldCheck,
  BookOpen,
  Lightbulb,
  Heart,
  Rocket,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  Globe,
  Building2
} from "lucide-react";
import { cmsAPI } from "../utils/api";

const iconRegistry = {
  users: Users,
  award: Award,
  book: BookOpen,
  star: Star,
  lightbulb: Lightbulb,
  shield: ShieldCheck,
  heart: Heart,
  target: Target,
  globe: Globe,
  rocket: Rocket,
  zap: Zap,
};

const AboutUs = () => {
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    cmsAPI.page('about')
      .then(({ data }) => setCmsPage(data.data))
      .catch((error) => {
        if (error.code !== 'ERR_CANCELED' && error.response?.status !== 404) console.error('Unable to load about page CMS.');
      });

    return () => controller.abort();
  }, []);

  const section = (type) => cmsPage?.sections?.find((item) => item.type === type)?.content || {};
  const hero = section('hero');
  const vision = section('about_vision');
  const services = section('about_services');
  const story = section('about_story');
  const achievementsSection = section('about_achievements');
  const valuesSection = section('about_values');
  const college = section('about_college');
  const team = section('about_team');
  const partnersSection = section('about_partners');
  const cta = section('cta');

  const coreValues = valuesSection.items?.length ? valuesSection.items.map((item) => ({
    icon: iconRegistry[item.icon] || Lightbulb,
    title: item.title,
    description: item.description,
  })) : [
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "Constantly evolving with AI-powered tools and modern learning methods"
    },
    {
      icon: ShieldCheck,
      title: "Trust & Transparency",
      description: "SEBI-certified mentors and government-recognized platform"
    },
    {
      icon: Heart,
      title: "Student First",
      description: "Every decision we make puts our learners' success first"
    },
    {
      icon: Target,
      title: "Result-Oriented",
      description: "Practical, hands-on learning that delivers real results"
    }
  ];

  const stats = section('stats').items?.length ? section('stats').items.map((item) => ({
    value: item.value,
    label: item.label,
    icon: iconRegistry[item.icon] || Users,
  })) : [
    { value: "35,000+", label: "Learners Trained", icon: Users },
    { value: "50+", label: "Expert Mentors", icon: Award },
    { value: "100+", label: "Courses & Programs", icon: BookOpen },
    { value: "4.9", label: "Average Rating", icon: Star }
  ];

  const teamMembers = team.members?.length ? team.members : [
    {
      name: "Trading Expert",
      role: "Lead Mentor",
      expertise: "Options & Derivatives"
    },
    {
      name: "Investment Advisor",
      role: "Wealth Management",
      expertise: "Portfolio Strategy"
    },
    {
      name: "Technical Analyst",
      role: "Chart Expert",
      expertise: "Price Action & Patterns"
    },
    {
      name: "AI Engineer",
      role: "Tech Lead",
      expertise: "AI-Powered Solutions"
    }
  ];

  const partners = partnersSection.items?.length ? partnersSection.items : [
    "NSE Academy",
    "BSE Institute",
    "NISM Certified",
    "MSME Registered",
    "Startup India"
  ];

  const achievements = achievementsSection.items?.length ? achievementsSection.items : [
    "Government-Recognized & MSME Certified Startup",
    "Powered by SEBI-certified trainers and AI engineers",
    "Trusted by 35,000+ Learners & Traders across India",
    "Partnered with 20+ Colleges and Universities",
    "Featured in leading financial publications"
  ];

  const serviceItems = services.items?.length ? services.items.map((item) => ({
    icon: iconRegistry[item.icon] || BookOpen,
    title: item.title,
    desc: item.description,
  })) : [
    {
      icon: BookOpen,
      title: "Educate",
      desc: "Wealth creation and management through proper risk and reward-based strategies"
    },
    {
      icon: Globe,
      title: "Build Ecosystem",
      desc: "Learning about financial markets with practical exposure and live trading sessions"
    },
    {
      icon: Rocket,
      title: "Empower",
      desc: "AI-powered tools, mentorship, and wealth management solutions for all"
    }
  ];

  const storyParagraphs = story.paragraphs?.length ? story.paragraphs : [
    'iStockly was born out of a simple question: "Why isn\'t stock market education practical, affordable, and accessible for all?"',
    "What started as a vision to simplify trading for students and retail investors has now become one of India's fastest-growing platforms. We've helped thousands of learners bridge the gap between theory and real market experience — making learning more interactive, practical, and rewarding.",
  ];

  const collegeItems = college.items?.length ? college.items : [
    "Hosting workshops, seminars, and trading bootcamps",
    "Providing access to live trading sessions & mentorship",
    "Helping colleges build financial literacy cells",
    "Creating opportunities to practice, compete & win"
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none">
        <motion.div
          animate={{ rotate: 360, scale: [1, 1.2, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-20 right-20 w-64 h-64 bg-yellow-400/5 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ rotate: -360, y: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-40 left-20 w-48 h-48 bg-yellow-400/10 rounded-full blur-2xl"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 space-y-24">

        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center space-y-6 pt-8"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 bg-yellow-400/10 border border-yellow-400/20 rounded-full text-yellow-400 text-sm font-medium"
          >
            <Rocket className="w-4 h-4 mr-2" />
            {hero.eyebrow || "India's #1 Stock Market Learning Platform"}
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold"
          >
            {hero.heading || 'About'} <span className="text-yellow-400">{hero.highlight || 'iStockly'}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-200 max-w-3xl mx-auto px-2"
          >
            {hero.subtitle || "India's 1st Live Stock Market Learning & AI-Powered Wealth Management Platform"}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-4xl mx-auto text-gray-400 text-lg leading-relaxed"
          >
            {hero.body || "At iStockly, we believe the stock market is not just for a few — it's for everyone. Our mission is to democratize financial education and empower individuals to learn, practice, and grow their wealth with confidence."}
            At iStockly, we believe the stock market is not just for a few — it's
            for everyone. Our mission is to democratize financial education and
            empower individuals to learn, practice, and grow their wealth with
            confidence.
          </motion.p>
        </motion.section>

        {/* Stats Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 rounded-xl p-4 sm:p-6 text-center hover:border-yellow-500/50 transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-yellow-400/10 rounded-full mb-2 sm:mb-3">
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-400" />
                </div>
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-xs sm:text-sm">{stat.label}</div>
              </motion.div>
            );
          })}
        </motion.section>

        {/* Vision & Mission */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6"
        >
          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-8 space-y-4 hover:border-yellow-500/30 transition-all duration-300"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-400/10 rounded-xl">
              <Eye className="w-7 h-7 text-yellow-400" />
            </div>
            <h3 className="text-2xl font-bold">{vision.vision_title || 'Our Vision'}</h3>
            <p className="text-gray-400 leading-relaxed">
              {vision.vision_body || 'To become the one-stop solution for all trading, wealth management, and market-related financial needs in India — empowering individuals with knowledge, technology, and confidence to achieve financial freedom.'}
              To become the one-stop solution for all trading, wealth management,
              and market-related financial needs in India — empowering
              individuals with knowledge, technology, and confidence to achieve
              financial freedom.
            </p>
          </motion.div>

          <motion.div
            whileHover={{ y: -5 }}
            className="bg-gradient-to-br from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-8 space-y-4 hover:border-yellow-500/30 transition-all duration-300"
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-400/10 rounded-xl">
              <Target className="w-7 h-7 text-yellow-400" />
            </div>
            <h3 className="text-2xl font-bold">{vision.mission_title || 'Our Mission'}</h3>
            <p className="text-gray-400 leading-relaxed">
              {vision.mission_body || 'To build a generation of financially literate and independent Indian youth by providing technology-driven learning, wealth management, and alternative sources of income through trading, investing, and AI-powered solutions.'}
              To build a generation of financially literate and independent
              Indian youth by providing technology-driven learning, wealth
              management, and alternative sources of income through trading,
              investing, and AI-powered solutions.
            </p>
          </motion.div>
        </motion.section>

        {/* What We Do */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-br from-zinc-900 via-zinc-900/80 to-black border border-zinc-800 rounded-2xl p-8 md:p-12 overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-yellow-400/5 rounded-full blur-3xl" />
          
          <div className="relative">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-yellow-400" />
              </div>
              <h2 className="text-3xl font-bold">{services.heading || 'What We Do'}</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {serviceItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-black/50 border border-zinc-800 rounded-xl p-6 hover:border-yellow-500/30 transition-all"
                  >
                    <Icon className="w-8 h-8 text-yellow-400 mb-4" />
                    <h4 className="text-lg font-semibold mb-2">{item.title}</h4>
                    <p className="text-gray-400 text-sm">{item.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </motion.section>

        {/* Our Story */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center space-y-6"
        >
          <h2 className="text-3xl md:text-4xl font-bold">{story.heading || 'Our Story'}</h2>
          <div className="space-y-4 text-gray-400 text-lg leading-relaxed">
            {storyParagraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            {false && <p>
              iStockly was born out of a simple question: <span className="text-yellow-400 font-medium">"Why isn't stock market
              education practical, affordable, and accessible for all?"</span>
            </p>}
            {false && <p>
              What started as a vision to simplify trading for students and retail
              investors has now become one of India's fastest-growing platforms.
              We've helped thousands of learners bridge the gap between theory and
              real market experience — making learning more interactive,
              practical, and rewarding.
            </p>}
          </div>
        </motion.section>

        {/* Why Choose Us */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-yellow-400/10 to-transparent border border-yellow-500/20 rounded-2xl p-8 md:p-12"
        >
          <h2 className="text-3xl font-bold mb-8 flex items-center gap-3">
            <Award className="w-8 h-8 text-yellow-400" />
            {achievementsSection.heading || 'Why Choose iStockly?'}
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-3"
              >
                <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">{achievement}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Core Values */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl font-bold text-center mb-10">{valuesSection.heading || 'Our Core Values'}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center hover:border-yellow-500/50 transition-all duration-300 group"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-400/10 rounded-xl mb-4 group-hover:bg-yellow-400/20 transition-colors">
                    <Icon className="w-7 h-7 text-yellow-400" />
                  </div>
                  <h4 className="text-lg font-semibold mb-2">{value.title}</h4>
                  <p className="text-gray-400 text-sm">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* College Collaboration */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 md:p-12"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-yellow-400" />
            </div>
            <h2 className="text-3xl font-bold">{college.heading || 'Collaboration with Colleges'}</h2>
          </div>

          <p className="text-gray-400 text-lg mb-6">
            {college.body || "We strongly believe that financial literacy must start young. That's why iStockly has partnered with leading colleges and universities to integrate stock market learning into student life."}
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {collegeItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-3 bg-black/50 rounded-lg p-4"
              >
                <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                <span className="text-gray-300">{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Team Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-8"
        >
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">{team.heading || 'Meet Our Team'}</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              {team.subtitle || "At the heart of iStockly is a passionate team of market experts, educators, and innovators working together to build India's most trusted financial learning platform."}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {teamMembers.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center hover:border-yellow-500/50 transition-all duration-300 group"
              >
                <div className="w-20 h-20 mx-auto bg-gradient-to-br from-yellow-400/20 to-yellow-400/5 rounded-full mb-4 flex items-center justify-center group-hover:from-yellow-400/30 group-hover:to-yellow-400/10 transition-all">
                  <Users className="w-10 h-10 text-yellow-400/70" />
                </div>
                <h4 className="font-semibold text-white mb-1">{member.name}</h4>
                <p className="text-yellow-400 text-sm mb-1">{member.role}</p>
                <p className="text-gray-500 text-xs">{member.expertise}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Partners Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="space-y-8"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-400/10 rounded-xl flex items-center justify-center">
              <Handshake className="w-6 h-6 text-yellow-400" />
            </div>
            <h2 className="text-3xl font-bold">{partnersSection.heading || 'Our Partners & Certifications'}</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {partners.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                className="h-20 bg-zinc-900 border border-zinc-800 rounded-xl flex items-center justify-center text-gray-400 font-medium hover:border-yellow-500/50 hover:text-yellow-400 transition-all duration-300"
              >
                {partner}
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative bg-gradient-to-br from-yellow-400/20 via-yellow-400/10 to-transparent border border-yellow-500/30 rounded-2xl p-8 md:p-12 text-center overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 to-transparent" />
          
          <div className="relative">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {cta.heading || 'Ready to Start Your Journey?'}
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              {cta.body || 'Join thousands of learners who have transformed their financial future with iStockly.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to={cta.primary_url || '/courses'}
                  className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-semibold rounded-lg hover:bg-yellow-300 transition-all shadow-lg hover:shadow-yellow-400/50"
                >
                  {cta.primary_label || 'Explore Courses'} <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to={cta.secondary_url || '/live/webinars'}
                  className="inline-flex items-center px-8 py-4 bg-white/10 border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all"
                >
                  {cta.secondary_label || 'Join Live Sessions'}
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.section>

      </div>
    </div>
  );
};

export default AboutUs;
